import dayjs, { Dayjs } from "dayjs";
import { Box, Button, Container, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DownloadIcon from "@mui/icons-material/Download";
import { useContext, useEffect, useState } from "react";
import { SuccessAlertContext } from "App";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { HelpOutline } from "@mui/icons-material";
import useSystemUsageDownload from "../hooks/useSystemUsageDownload";
import IncrementalSearchElement from "../components/IncrementalSearchElement";
import useSMSDownload from "../hooks/useSMSDownload";
import downloadHelpPdf from "../api/downloadHelpPdf";

// type Props = {
//   storehouse?: string;
//   storehouseOptions?: Storehouse[];
//   setStorehouse: Dispatch<SetStateAction<string>>;
//   setSupplier: Dispatch<SetStateAction<string>>;
//   setStorehouseSupplier: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>;
// };

// const handleStorehouseChange = (
//   value: string | null,
//   options: Storehouse[] | undefined,
//   setSh: Dispatch<SetStateAction<string>>,
//   setSup: Dispatch<SetStateAction<string>>,
//   setShSup: Dispatch<SetStateAction<StorehouseSupplierOptionsWithBlank>>,
// ) => {
//   const list = options ?? [];
//   if (value) {
//     setSh(list.filter((sh) => sh.name === value)[0].name);
//     setSup("");
//     setShSup("倉庫");
//   } else {
//     setSh("");
//     setShSup("");
//   }
// };

/**
 * レポートダウンロードページ
 * returns JSX.Element
 */
function SMSPage() {
  const {showAlert} = useContext(SuccessAlertContext);
  const {
    targetDate,
    setTargetDate,
    storeCd,
    setStoreCd,
    isLoading,
    handleDownload,
  } = useSMSDownload(showAlert);

  const parseYearMonth = (str: string) => dayjs(str, 'YYYY年M月');
  const formatYmDayjs = (date: Dayjs) => date.format('YYYY年M月');
  const formatYmDate = (date: Date) => formatYmDayjs(parseYearMonth(`${date.getFullYear()}-${date.getMonth() + 1}`));
  const now = new Date();
  // console.log(formatYmDate(new Date(now.getFullYear(), now.getMonth(), 1)));

  const dateOptions = [
    formatYmDate(new Date(now.getFullYear(), now.getMonth(), 1)), // 今月
    formatYmDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)), // 先月
  ];

  useEffect(() => {
    setTimeout(() => {
      (async () => {
        const userType = localStorage.getItem("user_type");
        if (!(userType === "C0001")) {
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: "本部ユーザーでログインしてください。再度ログインしてください。",
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        } else if (checkAccessTokenExpired()) {
          // 期限切れなら
          Notify?.setNotification?.({
            show: true,
            title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
            message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
          });
          Notify.onClose = () => {
            localStorage.clear();
            window.location.href = "/login";
          };
        }
      })();
    }, 0);
  }, []);

  return (
    <Box position="relative">
      <Box width="100%" height={32} textAlign="right" position="absolute" top={0}>
        <Button variant="custom-outlined" onClick={async (e) => {
          const url = await downloadHelpPdf(`${getUserType()}/resources/reports/manual_sms_report.pdf`);
          window.open(url, "_blank", "noopener,noreferrer");
        }} startIcon={<HelpOutline color="secondary" />} sx={{height: 24, padding: 1, marginTop: 0.5, marginRight: 1}}>ヘルプ</Button>
      </Box>
      <Box component="header" sx={{ display: "flex", alignItems: "center", px: 2, py: 4 }}>
        <Button
          startIcon={<ArrowBackIosNewIcon />}
          onClick={() => window.history.back()}
          sx={{
            color: "#000",
            textTransform: "none",
            border: "1px solid #ffa500",
            borderRadius: 1,
            mr: 2,
            minWidth: "6rem",
          }}
        >
          戻る
        </Button>
        <Grid container display="flex" flexGrow={1} alignItems="center" justifyContent="center">
          <DownloadIcon sx={{ fontSize: "2rem", color: "text.secondary", mr: 2 }} />
          <Typography
            variant="h5"
            component="h1"
            color="text.secondary"
            sx={{
              textAlign: "center",
              m1: 2,
              fontWeight: 700,
              fontSize: "1.8rem",
            }}
          >
            SMS送信件数ダウンロード
          </Typography>
        </Grid>
      </Box>

      <Container maxWidth="md">
        <Box mb={2} display="flex" alignItems="center" justifyContent="center">
          { (getUserType() === "C0001" || getUserType() === "C0006") &&
            <IncrementalSearchElement value={storeCd} setValue={setStoreCd} />
          }
          {/* 修正箇所 2025/05/20 */}
          {/* <TextField
            fullWidth
            label=" 店舗名"
            placeholder="店舗名を入力"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            sx={{ bgcolor: "#fff", width: 400 }}
          /> */}
        </Box>

        <Box sx={{ p: 4, borderRadius: 2, boxShadow: 1, mb: 2, maxWidth: 800, mx: "auto" }}>
          <Grid container justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1" color="text.secondary" fontWeight={700} gutterBottom>
              期間を指定してCSVレポートを取得します
            </Typography>
          </Grid>
          {/* Date Area */}
          <Box textAlign="center">
            <FormControl sx={{ width: 200, minWidth: 160, maxWidth: 'none' }} required>
              <InputLabel
                id="ym-select-label"
                sx={{
                  top: "-6px",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -4px) scale(0.75)",
                  },
                  "& .MuiFormLabel-asterisk": {
                    color: "var(--red-800)",
                  },
                }}
              >
                月を選択
              </InputLabel>
              <Select
                labelId="ym-select-label"
                label="月を選択"
                id="ym-select"
                placeholder="月を選択"
                value={targetDate ? (() => formatYmDayjs(targetDate))() : ''}
                onChange={(e) =>
                  setTargetDate(parseYearMonth((e.target.value ?? '').split('年').join('-').split('月')[0]))
                }
                sx={{ height: "40px" }}
              >
                {
                  // eslint-disable-next-line react/self-closing-comp
                  <MenuItem key="" value="">
                    &emsp;
                  </MenuItem>
                }
                {dateOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Grid
            item
            xs={12}
            sx={{
              textAlign: "right",
              mt: "2",
            }}
          >
            <Button
              variant="contained"
              disabled={isLoading}
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                bgcolor: "#ffa500",
                color: "#000000",
                ":hover": { bgcolor: "#e07b00" },
                textTransform: "none",
              }}
            >
              {isLoading ? "ダウンロード中" : "ダウンロード"}
            </Button>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default SMSPage;
